import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AppComponentBase } from '@shared/common/app-component-base';
import { TaskServiceProxy } from '@shared/service-proxies/service-proxies';
import { AppConsts } from '@shared/commonHelper/AppConsts';
import { BodyParking } from '@shared/helpers/BodyParking';

/**
 * Shape of the event emitted by `okClick`.
 *
 *   mode = 'cancel'              — user dismissed any of the dialogs
 *   mode = 'delete'              — single-task delete (no dependents)
 *   mode = 'delete-with-children' — parent task: Delete all (delete deps too)
 *   mode = 'keep-children'       — parent task: Keep tasks (unlink deps, then delete)
 */
export interface TaskDeleteConfirmResult {
  mode: 'cancel' | 'delete' | 'delete-with-children' | 'keep-children';
  obj: any;
}

/**
 * Shared task-delete dialog router.
 *
 * Wraps the two delete confirmation flows the app already uses on the edit-task
 * modal (parent-task → "Delete all / Keep tasks", non-parent → standard
 * `<vf-delete-popup>`) so every task surface — dependencies tab, view project
 * modal, my-day, edit-contact, edit-company, global search — can share a single
 * dialog implementation instead of replicating the routing logic inline.
 *
 * Usage:
 *   <vf-task-delete-confirm
 *     [task]="delTaskItem"
 *     [isOpenFromSlider]="true"
 *     (okClick)="onTaskDelete($event)">
 *   </vf-task-delete-confirm>
 *
 * Then assign `delTaskItem` to the task you want to delete (a new reference each
 * time — see `ngOnChanges` below). The component:
 *   1. Inspects `task.tasks` if present; otherwise calls `GetTaskById` to fetch
 *      it. The latter handles surfaces (my-day, project list, global search)
 *      whose row DTOs don't include the dependent list.
 *   2. Shows the parent-task confirm modal when dependents > 0 OR the standard
 *      vf-delete-popup when none.
 *   3. Emits `okClick` with a TaskDeleteConfirmResult so the caller can run the
 *      appropriate API calls (deleteTasks vs deleteTask vs unlink+delete).
 *
 * The caller still owns the actual delete API call — this component only
 * orchestrates the UX. That keeps the component free of caller-specific
 * side-effects (toasts, refresh signals, list mutations, etc.).
 */
@Component({
  selector: 'vf-task-delete-confirm',
  templateUrl: './task-delete-confirm.component.html'
})
export class TaskDeleteConfirmComponent extends AppComponentBase implements OnChanges, AfterViewInit, OnDestroy {
  @Input() task: any | null = null;
  @Input() isOpenFromSlider = false;
  @Output() okClick = new EventEmitter<TaskDeleteConfirmResult>();

  @ViewChild('parentConfirmModal') parentConfirmModal: ModalDirective;
  @ViewChild('parentConfirmModal', { read: ElementRef }) private parentConfirmModalEl?: ElementRef<HTMLElement>;

  /** The parent-task confirm opens in the middle of the screen wherever this is declared, like the
   *  vf-delete-popup beside it (which parks itself). See BodyParking. */
  private readonly parentConfirmParking: BodyParking;
  private readonly destroy$ = new Subject<void>();

  /** The resolved task passed down to <vf-delete-popup> — null when the parent
   *  modal is showing instead. Setting this to a new reference triggers
   *  delete-popup's own ngOnChanges. */
  regularDeleteTarget: any | null = null;

  /** Cached resolved task for the parent-confirm modal (so the title is correct
   *  even if `task` is replaced during the modal's lifetime). */
  parentTarget: any | null = null;

  private isResolving = false;

  constructor(
    injector: Injector,
    private _taskService: TaskServiceProxy,
    renderer: Renderer2,
    @Inject(DOCUMENT) doc: Document
  ) {
    super(injector);
    this.parentConfirmParking = new BodyParking(renderer, doc, () => this.parentConfirmModalEl?.nativeElement);
  }

  ngAfterViewInit(): void {
    this.parentConfirmModal.onHidden.pipe(takeUntil(this.destroy$)).subscribe(() => this.parentConfirmParking.unpark());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.parentConfirmParking.unpark();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.task) return;
    const next = changes.task.currentValue;
    if (!next) return;
    this.routeDeleteDecision(next);
  }

  /**
   * Decide whether to show parent confirm vs regular delete.
   *
   * If `task.tasks` is already an array (edit-task-modal, dependencies tab when
   * caller passes the enriched object), inspect it directly — no extra round
   * trip needed. Otherwise call GetTaskById to ask the BE — that's the path
   * surfaces like my-day, project task list, and global search will hit.
   *
   * The id lookup falls back across two naming conventions: `id` (standard task
   * surfaces) and `taskId` (DependencyRow on the dependencies tab, contributor
   * rows, and a few list DTOs). Without this fallback the component silently
   * bails for callers that only have `taskId` on their row shape.
   */
  private routeDeleteDecision(task: any): void {
    // Feature flag — when Task Dependencies is disabled, the parent-task confirm
    // ("Delete all / Keep tasks") doesn't apply. Always show the regular delete
    // popup so every surface deletes a task with the standard confirmation.
    if (!AppConsts.dependenciesFeatureEnabled) {
      const target = { ...task };
      if (!target.id) target.id = task?.id ?? task?.taskId;
      this.show(target, false);
      return;
    }
    // Fast path — the caller already supplied the dependents array (e.g. the
    // edit-task-modal's taskItem, which getdependency() populates). Trust it.
    if (Array.isArray(task?.tasks)) {
      this.show(task, task.tasks.length > 0);
      return;
    }
    const taskId = task?.id ?? task?.taskId;
    if (!taskId) return;
    if (this.isResolving) return;
    this.isResolving = true;

    // "Is this a parent task?" is answered by GetDependentTasks, NOT GetTaskById.
    // GetTaskById's payload does not include the task's dependents — those live in
    // a separate endpoint (the same one edit-task-modal.getdependency() uses). The
    // earlier implementation checked GetTaskById().tasks, which is always empty, so
    // it always fell through to the plain delete dialog. Fetch the real dependents.
    this._taskService.getDependentTasks(taskId).subscribe(
      result => {
        this.isResolving = false;
        const deps = (result && !result.isError && result.responseData) ? result.responseData : [];
        // Build the target object the dialogs + caller handlers expect. Attach the
        // dependents under `tasks` (each carrying `taskId`) so the parent-confirm
        // "Delete all" path can collect their ids for the bulk delete.
        const target: any = { ...task };
        if (!target.id) target.id = taskId;
        target.tasks = deps.map((d: any) => ({ ...d, taskId: d.taskId }));
        this.show(target, deps.length > 0);
      },
      () => {
        // Lookup failed — fall back to the regular delete dialog so the user
        // isn't blocked. The BE will cascade dependents cleanup on its own
        // (DeleteAndUpdateTaskDependency) regardless.
        this.isResolving = false;
        const fallback = { ...task };
        if (!fallback.id) fallback.id = taskId;
        this.show(fallback, false);
      }
    );
  }

  private show(target: any, hasDependents: boolean): void {
    if (hasDependents) {
      this.parentTarget = target;
      this.regularDeleteTarget = null;
      setTimeout(() => {
        this.parentConfirmParking.park();
        this.parentConfirmModal?.show();
      }, 0);
    } else {
      this.parentTarget = null;
      // New reference so vf-delete-popup's ngOnChanges fires reliably even if
      // we just dismissed it for the same task earlier.
      this.regularDeleteTarget = { ...target };
    }
  }

  // ---- Parent-confirm modal handlers ----

  onDeleteAll(): void {
    const obj = this.parentTarget;
    this.parentConfirmModal?.hide();
    this.parentTarget = null;
    this.okClick.emit({ mode: 'delete-with-children', obj });
  }

  onKeepTasks(): void {
    const obj = this.parentTarget;
    this.parentConfirmModal?.hide();
    this.parentTarget = null;
    this.okClick.emit({ mode: 'keep-children', obj });
  }

  onCloseParentConfirm(): void {
    const obj = this.parentTarget;
    this.parentConfirmModal?.hide();
    this.parentTarget = null;
    this.okClick.emit({ mode: 'cancel', obj });
  }

  // ---- Regular vf-delete-popup handler ----
  // The vf-delete-popup emits { toggle, obj } via its okClick — we translate
  // that into our richer mode enum so callers have a single switch statement.

  onRegularDelete(ev: { toggle: boolean; obj: any }): void {
    const obj = ev?.obj;
    this.regularDeleteTarget = null;
    if (ev?.toggle) {
      this.okClick.emit({ mode: 'delete', obj });
    } else {
      this.okClick.emit({ mode: 'cancel', obj });
    }
  }
}
